import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Sync slideshow disabled state for any layout already applied (e.g. restored from sessionStorage via inline script)
    const { grid } = this.refs;
    if (grid) {
      this.#syncAlternativeEvenSlideshows(grid.getAttribute('product-grid-view') || 'default');
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const slideshows = /** @type {import('slideshow').Slideshow[]} */ ([...grid.querySelectorAll('slideshow-component')]);
    const savedSlides = new Map(slideshows.map((slideshow) => [slideshow, slideshow.current]));

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    this.#restoreSlides(savedSlides);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Restores card slideshows to the slides they showed before a layout change.
   * The product-grid view transition sets content-visibility: hidden on off-screen cards, so their
   * scrollers re-snap to a wrong slide once revealed with the new card width.
   * Compares the real scroll position, because slideshow.current lags behind the re-snap until scrollend
   * (and select() is a no-op while current still equals the target). The scroll event that follows
   * syncs slideshow.current and aria-hidden.
   *
   * @param {Map<import('slideshow').Slideshow, number>} savedSlides - Slide index per slideshow before the layout change
   */
  #restoreSlides(savedSlides) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        for (const [slideshow, index] of savedSlides) {
          const slide = slideshow.slides?.[index];
          const { scroller } = slideshow.refs;
          if (!slide || !scroller || Math.abs(scroller.scrollLeft - slide.offsetLeft) < 1) continue;

          scroller.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
        }
      })
    );
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncAlternativeEvenSlideshows(value);
  }

  /**
   * Disables slideshow components for even product items when in mobile-alternative layout,
   * re-enables them otherwise. Prevents hover-preview and carousel swiping for the products
   * that show two images side-by-side in the alternative mobile layout.
   *
   * @param {string} layoutValue
   */
  #syncAlternativeEvenSlideshows(layoutValue) {
    const isMobileAlternative = layoutValue === 'mobile-alternative' && !mediaQueryLarge.matches;
    const evenItems = this.querySelectorAll('.product-grid__item:nth-of-type(even)');

    for (const item of evenItems) {
      const slideshow = item.querySelector('slideshow-component');
      if (!slideshow) continue;

      if (isMobileAlternative) {
        slideshow.setAttribute('disabled', 'true');
      } else {
        slideshow.removeAttribute('disabled');
      }
    }
  }

  /**
   * Handles the media query change event.
   * Restores the saved layout for the new viewport from sessionStorage.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const isDesktop = event.matches;
    const viewport = isDesktop ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';

    const radioName = isDesktop ? 'grid' : 'grid-mobile';
    const targetElement = this.querySelector(
      `input[type="radio"][name="${radioName}"][value="${storedLayout}"]`
    );
    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
    }

    this.#setLayout(storedLayout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
